import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { auth, db, googleProvider, githubProvider } from '../firebase';
import { Link } from 'react-router-dom';

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

function FloatInput({ id, label, type, value, onChange, placeholder }) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;
  return (
    <div style={{ position: 'relative', marginBottom: '14px' }}>
      <label htmlFor={id} style={{
        position: 'absolute', left: '14px',
        top: lifted ? '8px' : '50%', transform: lifted ? 'none' : 'translateY(-50%)',
        fontSize: lifted ? '10px' : '15px', color: focused ? '#6366f1' : '#94a3b8',
        fontWeight: 500, letterSpacing: lifted ? '0.05em' : '0',
        textTransform: lifted ? 'uppercase' : 'none',
        transition: 'all 0.18s', pointerEvents: 'none', zIndex: 1,
      }}>{label}</label>
      <input
        id={id} type={type} value={value} onChange={onChange}
        placeholder={focused ? placeholder : ''} required
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: lifted ? '22px 14px 7px' : '17px 14px',
          border: `1.5px solid ${focused ? '#6366f1' : '#e2e8f0'}`,
          borderRadius: '12px', fontSize: '16px', color: '#1e293b',
          backgroundColor: focused ? '#ffffff' : '#f8fafc', outline: 'none',
          transition: 'border-color 0.18s, box-shadow 0.18s, background-color 0.18s',
          boxShadow: focused ? '0 0 0 3px rgba(99,102,241,0.12)' : 'none',
          WebkitAppearance: 'none',
        }}
      />
    </div>
  );
}

function OAuthBtn({ icon, label, onClick, loading }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      disabled={loading}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        padding: '13px 14px', border: `1.5px solid ${hover ? '#cbd5e1' : '#e2e8f0'}`,
        borderRadius: '12px', background: hover ? '#f1f5f9' : '#ffffff',
        fontSize: '14px', fontWeight: 600, color: '#374151',
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s', WebkitAppearance: 'none', minHeight: '48px',
        opacity: loading ? 0.7 : 1,
      }}>
      <span style={{ fontSize: '17px' }}>{loading ? '⏳' : icon}</span>
      {loading ? 'Signing in…' : label}
    </button>
  );
}

async function handleOAuthNewUser(user) {
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    const orgRef = doc(collection(db, 'organizations'));
    await setDoc(orgRef, {
      name: `${user.displayName || user.email}'s Organisation`,
      ownerId: user.uid, createdAt: new Date(),
    });
    await setDoc(userRef, {
      name: user.displayName || '', email: user.email,
      role: 'admin', orgId: orgRef.id, createdAt: new Date(),
    });
  }
}

export default function Signup() {
  const isMobile = useIsMobile();
  const [orgName, setOrgName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [error, setError] = useState('');
  const [btnHover, setBtnHover] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault(); setError('');
    if (!orgName.trim() || !name.trim() || !email.trim() || !password.trim()) { setError('All fields are required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const uid = result.user.uid;
      const orgRef = doc(collection(db, 'organizations'));
      await setDoc(orgRef, { name: orgName.trim(), ownerId: uid, createdAt: new Date() });
      await setDoc(doc(db, 'users', uid), { name: name.trim(), email: email.trim(), role: 'admin', orgId: orgRef.id, createdAt: new Date() });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('This email is already registered. Please sign in.');
      else if (err.code === 'auth/invalid-email') setError('Invalid email address.');
      else setError('Failed to create account. Please try again.');
      setLoading(false);
    }
  };

const handleGoogle = async () => {
  setError(''); setOauthLoading('google');
  try {
    const r = await signInWithPopup(auth, googleProvider);
    await handleOAuthNewUser(r.user);
  } catch (err) {
    const ignored = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];
    if (!ignored.includes(err.code)) {
      if (err.code === 'auth/popup-blocked')
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      else
        setError('Google sign-up failed. Please try again.');
    }
  } finally { setOauthLoading(''); }
};

const handleGithub = async () => {
  setError(''); setOauthLoading('github');
  try {
    const r = await signInWithPopup(auth, githubProvider);
    await handleOAuthNewUser(r.user);
  } catch (err) {
    const ignored = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];
    if (!ignored.includes(err.code)) {
      if (err.code === 'auth/popup-blocked')
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      else
        setError('GitHub sign-up failed. Please try again.');
    }
  } finally { setOauthLoading(''); }
};

  const submitBtn = (disabled) => ({
    width: '100%', padding: '16px', marginTop: '6px', minHeight: '52px',
    background: disabled ? '#a5b4fc' : btnHover ? 'linear-gradient(135deg,#4f46e5,#6366f1)' : 'linear-gradient(135deg,#6366f1,#818cf8)',
    color: '#fff', border: 'none', borderRadius: '12px',
    fontSize: '16px', fontWeight: 700, fontFamily: "'Syne',sans-serif",
    cursor: disabled ? 'not-allowed' : 'pointer', letterSpacing: '-0.01em',
    boxShadow: disabled ? 'none' : '0 4px 16px rgba(99,102,241,0.4)',
    transition: 'all 0.18s', transform: btnHover && !disabled ? 'translateY(-1px)' : 'none',
    WebkitAppearance: 'none',
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { -webkit-text-size-adjust: 100%; overflow-x: hidden; }
        @keyframes floatA{0%,100%{transform:translateY(0) rotate(-3deg)}50%{transform:translateY(-12px) rotate(-3deg)}}
        @keyframes floatB{0%,100%{transform:translateY(0) rotate(3deg)}50%{transform:translateY(-10px) rotate(3deg)}}
        @keyframes floatC{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
      `}</style>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: '100vh', minHeight: '-webkit-fill-available' }}>

        {/* ══ LEFT / TOP BRANDING ══ */}
        <div style={{
          flex: isMobile ? 'none' : 1, position: 'relative',
          background: 'linear-gradient(135deg, #020817 0%, #0c1a3a 25%, #111827 45%, #1a1040 65%, #0f2d5a 85%, #082f4a 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '40px 24px 48px' : '56px',
          overflow: 'hidden', minHeight: isMobile ? 'auto' : '100vh',
        }}>
          {/* Blobs */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-10%', right: '-8%', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.26) 0%, transparent 68%)' }} />
            <div style={{ position: 'absolute', bottom: '-12%', left: '-8%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 65%)' }} />
            <div style={{ position: 'absolute', top: '42%', left: '-5%', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 65%)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 60% at 46% 50%, transparent 15%, rgba(2,8,23,0.80) 100%)' }} />
          </div>

          {/* Floating cards — desktop only */}
          {!isMobile && (<>
            <div style={{ position: 'absolute', top: '8%', right: '5%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(165,180,252,0.6)', borderRadius: '16px', padding: '16px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', animation: 'floatA 5.5s ease-in-out infinite', minWidth: '210px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🚀</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#6366f1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Up in 60 seconds</span>
              </div>
              {['Create account', 'Add products', 'Invite team'].map((step, i) => (
                <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: i < 2 ? '8px' : '0' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: i < 2 ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: i < 2 ? '#fff' : '#94a3b8', fontWeight: 700, flexShrink: 0 }}>{i < 2 ? '✓' : '3'}</div>
                  <span style={{ fontSize: '12px', color: i < 2 ? '#0f172a' : '#94a3b8', fontWeight: i < 2 ? 600 : 400 }}>{step}</span>
                </div>
              ))}
            </div>

            <div style={{ position: 'absolute', top: '38%', right: '3%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(134,239,172,0.6)', borderRadius: '16px', padding: '16px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', animation: 'floatB 6.5s ease-in-out infinite', minWidth: '196px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🎁</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#059669', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Always Free</span>
              </div>
              {['Unlimited products', 'Unlimited members', 'All features'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <span style={{ fontSize: '11px' }}>✅</span>
                  <span style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>{t}</span>
                </div>
              ))}
            </div>

            <div style={{ position: 'absolute', bottom: '10%', right: '5%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(253,211,77,0.6)', borderRadius: '16px', padding: '16px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', animation: 'floatC 7s ease-in-out infinite', minWidth: '190px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>👥</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#d97706', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Role-based Access</span>
              </div>
              {[{ role: 'Admin', color: '#3b82f6', bg: '#eff6ff' }, { role: 'Manager', color: '#8b5cf6', bg: '#f5f3ff' }, { role: 'Staff', color: '#10b981', bg: '#f0fdf4' }].map(({ role, color, bg }) => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ fontSize: '12px', color: '#374151' }}>{role}</span>
                  <span style={{ fontSize: '10px', fontWeight: 700, color, background: bg, padding: '2px 8px', borderRadius: '6px' }}>Active</span>
                </div>
              ))}
            </div>

            <div style={{ position: 'absolute', top: '62%', left: '2%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(252,165,165,0.5)', borderRadius: '16px', padding: '14px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', animation: 'floatA 7.5s ease-in-out infinite 1s', minWidth: '178px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📥</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Excel Import</span>
              </div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '3px' }}>inventory.xlsx</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>243 products imported ✓</div>
            </div>
          </>)}

          {/* Main content */}
          <div style={{ position: 'relative', zIndex: 2, maxWidth: isMobile ? '100%' : '500px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '20px' : '32px' }}>
              <div style={{ width: isMobile ? '38px' : '44px', height: isMobile ? '38px' : '44px', borderRadius: '12px', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '18px' : '22px', boxShadow: '0 4px 20px rgba(99,102,241,0.55)', flexShrink: 0 }}>📦</div>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>StockSpace</span>
            </div>

            {isMobile ? (
              <div>
                <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '34px', fontWeight: 800, color: '#fff', lineHeight: 1.12, letterSpacing: '-0.03em', marginBottom: '12px' }}>
                  Inventory control.<br />
                  <span style={{ background: 'linear-gradient(90deg,#a78bfa,#60a5fa,#34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Starts free.</span>
                </h1>
                <p style={{ fontSize: '15px', color: '#94a3b8', lineHeight: 1.65, marginBottom: '20px' }}>
                  Free forever. No credit card. Up and running in 60 seconds.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { icon: '🎁', text: '100% Free', color: '#34d399', bg: 'rgba(52,211,153,0.14)', border: 'rgba(52,211,153,0.3)' },
                    { icon: '⚡', text: 'Real-time', color: '#fbbf24', bg: 'rgba(251,191,36,0.14)', border: 'rgba(251,191,36,0.3)' },
                    { icon: '👥', text: 'Team roles', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)', border: 'rgba(96,165,250,0.3)' },
                    { icon: '📥', text: 'Excel import', color: '#f87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.3)' },
                  ].map(({ icon, text, color, bg, border }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', background: bg, border: `1px solid ${border}`, borderRadius: '20px' }}>
                      <span style={{ fontSize: '13px' }}>{icon}</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color }}>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '58px', fontWeight: 800, color: '#fff', lineHeight: 1.08, letterSpacing: '-0.038em', marginBottom: '18px' }}>
                  Inventory control.<br />Team ready.<br />
                  <span style={{ background: 'linear-gradient(90deg,#a78bfa 0%,#60a5fa 50%,#34d399 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Starts free.</span>
                </h1>
                <p style={{ fontSize: '17px', color: '#94a3b8', lineHeight: 1.72, marginBottom: '30px', maxWidth: '400px' }}>
                  Join businesses using StockSpace to track stock, analyse sales, and collaborate —{' '}
                  <span style={{ color: '#e2e8f0', fontWeight: 500 }}>all completely free, forever.</span>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '28px' }}>
                  {[
                    { icon: '🎁', text: '100% Free', color: '#34d399', bg: 'rgba(52,211,153,0.14)', border: 'rgba(52,211,153,0.3)' },
                    { icon: '⚡', text: 'Real-time sync', color: '#fbbf24', bg: 'rgba(251,191,36,0.14)', border: 'rgba(251,191,36,0.3)' },
                    { icon: '📊', text: 'Smart analytics', color: '#a78bfa', bg: 'rgba(167,139,250,0.14)', border: 'rgba(167,139,250,0.3)' },
                    { icon: '👥', text: 'Team roles', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)', border: 'rgba(96,165,250,0.3)' },
                    { icon: '📥', text: 'Excel import', color: '#f87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.3)' },
                  ].map(({ icon, text, color, bg, border }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: bg, border: `1px solid ${border}`, borderRadius: '24px' }}>
                      <span style={{ fontSize: '14px' }}>{icon}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color }}>{text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { num: '01', text: 'Create your free account', color: '#a78bfa' },
                    { num: '02', text: 'Add products via Excel or manually', color: '#60a5fa' },
                    { num: '03', text: 'Invite your team and start tracking', color: '#34d399' },
                  ].map(({ num, text, color }) => (
                    <div key={num} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}40`, borderRadius: '6px', padding: '3px 8px', flexShrink: 0 }}>{num}</div>
                      <span style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: 500 }}>{text}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ══ RIGHT / BOTTOM — SIGNUP FORM ══ */}
        <div style={{
          width: isMobile ? '100%' : 'min(500px, 42vw)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          borderRadius: isMobile ? '24px 24px 0 0' : '0',
          marginTop: isMobile ? '-24px' : '0',
          padding: isMobile ? '32px 20px 0' : '40px 48px',
          paddingBottom: isMobile ? 'max(40px, env(safe-area-inset-bottom))' : '40px',
          backgroundColor: '#ffffff',
          position: 'relative', zIndex: 3,
          overflowY: 'auto', overflowX: 'hidden',
        }}>
          <div style={{ width: '100%', maxWidth: '380px', paddingTop: '8px' }}>

            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? '24px' : '28px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '5px' }}>Create your account</h2>
              <p style={{ fontSize: '14px', color: '#64748b' }}>Free forever — no credit card required</p>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 14px', borderRadius: '12px', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ flexShrink: 0 }}>⚠️</span> {error}
              </div>
            )}

            {/* OAuth */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <OAuthBtn icon="🔵" label="Google" onClick={handleGoogle} loading={oauthLoading === 'google'} />
              <OAuthBtn icon="⚫" label="GitHub" onClick={handleGithub} loading={oauthLoading === 'github'} />
            </div>

            {/* Info note */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#166534', lineHeight: 1.55 }}>
              💡 <strong>Quick signup:</strong> Google/GitHub creates your account and a default organisation instantly. Rename it anytime in Settings.
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1, height: '1px', background: '#f1f5f9' }} />
              <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' }}>or sign up with email</span>
              <div style={{ flex: 1, height: '1px', background: '#f1f5f9' }} />
            </div>

            <form onSubmit={handleSignup}>
              <FloatInput id="orgName" label="Business name" type="text" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. Sharma Traders" />
              <FloatInput id="uname" label="Your name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rahul Sharma" />
              <FloatInput id="email" label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="rahul@example.com" />
              <FloatInput id="password" label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" />
              <FloatInput id="confirm" label="Confirm password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" />

              {/* Password strength */}
              {password.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '-8px', marginBottom: '14px' }}>
                  {[1, 2, 3].map(level => {
                    const strength = password.length >= 10 ? 3 : password.length >= 6 ? 2 : 1;
                    return <div key={level} style={{ flex: 1, height: '3px', borderRadius: '2px', background: strength >= level ? ['#ef4444', '#f59e0b', '#22c55e'][strength - 1] : '#e2e8f0', transition: 'background 0.3s' }} />;
                  })}
                  <span style={{ fontSize: '10px', color: password.length >= 10 ? '#22c55e' : password.length >= 6 ? '#f59e0b' : '#ef4444', fontWeight: 600, marginLeft: '6px', whiteSpace: 'nowrap' }}>
                    {password.length >= 10 ? 'Strong' : password.length >= 6 ? 'Good' : 'Too short'}
                  </span>
                </div>
              )}

              <button type="submit" disabled={loading || oauthLoading !== ''}
                onMouseEnter={() => setBtnHover(true)} onMouseLeave={() => setBtnHover(false)}
                style={submitBtn(loading || oauthLoading !== '')}>
                {loading ? 'Creating account…' : 'Create Free Account →'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '14px', color: '#64748b', marginTop: '16px' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#6366f1', fontWeight: 700, textDecoration: 'none' }}>Sign in</Link>
            </p>

            {/* Trust badges */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '16px', paddingTop: '16px', paddingBottom: '8px', borderTop: '1px solid #f1f5f9' }}>
              {[{ icon: '🔒', text: 'Secure' }, { icon: '🎁', text: 'Free forever' }, { icon: '⚡', text: 'Instant setup' }].map(({ icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontSize: '13px' }}>{icon}</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}